/**
 * Visual progress for the learner lesson journey (non-sequential steps; final step gates on all others).
 */

import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';
import { Check, Circle, ChevronDown, ChevronRight, Loader2, Route } from 'lucide-react';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import {
  LessonJourneyContentIcon,
  lessonJourneyIconKindFromStepId
} from './LessonJourneyContentIcon';

export type JourneyStepStatus = 'complete' | 'current' | 'upcoming';

export interface LessonJourneyStep {
  id: string;
  label: string;
  hint?: string;
  status: JourneyStepStatus;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  /** Show a spinner beside the action while the parent request is in flight. */
  actionLoading?: boolean;
  /** Nested under “Review the lesson” (e.g. mandatory inline exercises). */
  children?: LessonJourneyStep[];
}

type JourneyExpandState = 'collapsed' | 'full';

const JOURNEY_FLASH_MS = 3000;

interface LessonJourneyTimelineProps {
  steps: LessonJourneyStep[];
  className?: string;
  /** When true, open fully on first mount. Always starts closed by default. */
  defaultExpanded?: boolean;
  /**
   * @deprecated Expand state is no longer persisted across visits — journey always starts closed.
   * Kept optional so existing call sites compile.
   */
  persistExpandLessonId?: number;
  /** Increment to scroll the journey into view and pulse-highlight (~3s). Does not force-expand. */
  highlightPulseRequest?: number;
  /** Increment to force the journey closed from the parent page. */
  collapseRequest?: number;
  /** Matches `data-tour-id` for product tours. */
  tourId?: string;
}

function journeyExpandAriaLabel(open: boolean, t: (key: string, options?: Record<string, unknown>) => string): string {
  return open
    ? t('journey.closeJourney', { defaultValue: 'Close lesson journey' })
    : t('journey.openJourney', { defaultValue: 'Open lesson journey' });
}

type CircleSize = 'md' | 'sm';

/** Hint / helper copy — slightly smaller on mobile. */
const journeyCommentClass =
  'text-[11px] leading-snug text-gray-500 dark:text-gray-400 sm:text-xs sm:leading-normal';

const journeyCommentAccentClass =
  'text-[11px] leading-snug sm:text-xs sm:leading-relaxed';

const JourneyPaperContext = React.createContext(false);

function useJourneyPaper(): boolean {
  return useContext(JourneyPaperContext);
}

function StepCircle({ status, size = 'md' }: { status: JourneyStepStatus; size?: CircleSize }) {
  const paper = useJourneyPaper();
  const dim = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';
  const iconDim = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const dotDim = size === 'sm' ? 'h-2 w-2' : 'h-3 w-3';

  if (status === 'complete') {
    return (
      <span
        className={`flex ${dim} shrink-0 items-center justify-center rounded-full ${
          paper
            ? 'bg-[#2f6b4f] text-[#fffdf8] ring-2 ring-[#d7e6d4] dark:bg-[#8fbf9a] dark:text-[#1c1714] dark:ring-[#2c4034]'
            : 'bg-green-600 text-white shadow-sm ring-2 ring-green-200 dark:ring-green-800'
        }`}
      >
        <Check className={iconDim} strokeWidth={2.5} aria-hidden />
      </span>
    );
  }
  if (status === 'current') {
    return (
      <span
        className={`flex ${dim} shrink-0 items-center justify-center rounded-full ${
          paper
            ? 'bg-[#3f2a56] text-[#fffdf8] ring-2 ring-[#e4d5c3] dark:bg-[#d4b48a] dark:text-[#1c1714] dark:ring-[#3d342c]'
            : 'bg-blue-600 text-white shadow-md ring-4 ring-blue-200/80 dark:ring-blue-800/60'
        }`}
      >
        <Circle className={`${dotDim} fill-current`} aria-hidden />
      </span>
    );
  }
  return (
    <span
      className={`flex ${dim} shrink-0 items-center justify-center rounded-full border-2 ${
        paper
          ? 'border-[#e4d5c3] bg-[#fffdf8] text-[#c4a574] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#8a6a45]'
          : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500'
      }`}
    >
      <Circle className={dotDim} aria-hidden />
    </span>
  );
}

function connectorColor(done: boolean, paper: boolean) {
  if (paper) {
    return done ? 'bg-[#2f6b4f] dark:bg-[#8fbf9a]' : 'bg-[#e4d5c3] dark:bg-[#3d342c]';
  }
  return done ? 'bg-green-500 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700';
}

function StepActionButton({
  step,
  isRedoAction
}: {
  step: LessonJourneyStep;
  isRedoAction: boolean;
}) {
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  if (!step.onAction || !step.actionLabel) return null;
  const paper = useJourneyPaper();
  const loading = Boolean(step.actionLoading);
  return (
    <button
      type="button"
      onClick={step.onAction}
      disabled={step.actionDisabled || loading}
      aria-busy={loading}
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
        paper
          ? isRedoAction
            ? 'border border-[#e4d5c3] bg-[#fffdf8] text-[#8a6a45] hover:bg-[#f7f1e8] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#d4b48a] dark:hover:bg-[#261f1b]'
            : step.status === 'current'
              ? 'bg-[#3f2a56] text-[#fff8ee] hover:bg-[#4a3264] dark:bg-[#d4b48a] dark:text-[#1c1714] dark:hover:bg-[#e4c9a4]'
              : 'bg-[#f7f1e8] text-[#3f2a56] hover:bg-[#efe4d4] dark:bg-[#261f1b] dark:text-[#f6efe6] dark:hover:bg-[#322922]'
          : isRedoAction
          ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-200 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-800/60 dark:hover:bg-amber-950/60'
          : step.status === 'current'
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
      }`}
    >
      {step.actionLabel}
      {loading ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
      ) : (
        <ChevronRight className={localeMirroredNavArrowClass(isRtl, 'h-3 w-3')} aria-hidden />
      )}
    </button>
  );
}

function StepBody({ step, nested = false }: { step: LessonJourneyStep; nested?: boolean }) {
  const paper = useJourneyPaper();
  const labelClass = paper
    ? step.status === 'complete'
      ? 'text-[#2f6b4f] dark:text-[#8fbf9a]'
      : step.status === 'current'
        ? 'text-[#3f2a56] dark:text-[#f6efe6]'
        : 'text-[#8a6a45] dark:text-[#d4b48a]'
    : step.status === 'complete'
      ? 'text-green-800 dark:text-green-300'
      : step.status === 'current'
        ? 'text-blue-800 dark:text-blue-200'
        : 'text-gray-700 dark:text-gray-300';

  const isRedoAction = step.status === 'complete' && Boolean(step.onAction);
  const iconKind = lessonJourneyIconKindFromStepId(step.id);

  return (
    <div className={nested ? 'min-w-0 flex-1' : 'min-w-0 flex-1 pb-0.5 pt-0.5'}>
      {!nested ? (
        <div className="flex items-start gap-2">
          {iconKind ? (
            <span className="mt-0.5 shrink-0">
              <LessonJourneyContentIcon
                kind={iconKind}
                className={paper ? '[&_svg]:text-[#3f2a56] dark:[&_svg]:text-[#d4b48a]' : ''}
                showPassTick={
                  step.status === 'complete' &&
                  (step.id === 'mark-passed' ||
                    step.id.startsWith('quiz-') ||
                    step.id.startsWith('exercise-'))
                }
              />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className={`font-medium leading-snug ${labelClass} text-sm sm:text-base`}>{step.label}</p>
            {step.hint ? (
              <p className={`mt-0.5 ${journeyCommentClass} ${paper ? '!text-[#8a6a45] dark:!text-[#c4a574]' : ''}`}>{step.hint}</p>
            ) : null}
            <div className={step.hint ? 'mt-2' : 'mt-1.5'}>
              <StepActionButton step={step} isRedoAction={isRedoAction} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InlineExerciseListItem({ child }: { child: LessonJourneyStep }) {
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const paper = useJourneyPaper();
  const done = child.status === 'complete';
  const labelClass = paper
    ? done
      ? 'text-[#2f6b4f] dark:text-[#8fbf9a]'
      : 'text-[#3f2a56] dark:text-[#f6efe6]'
    : done
      ? 'text-green-800 dark:text-green-300'
      : 'text-gray-800 dark:text-gray-200';
  const canOpen = Boolean(child.onAction);

  const handleOpen = () => {
    child.onAction?.();
  };

  return (
    <li
      className={`rounded-lg border px-3 py-2.5 ${paper ? '' : 'shadow-sm'} ${
        paper
          ? done
            ? 'border-[#d7e6d4] bg-[#f4f8f4] dark:border-[#2c4034] dark:bg-[#1a2420]'
            : 'border-[#e4d5c3] bg-[#fffdf8] dark:border-[#3d342c] dark:bg-[#1c1714]'
          : done
            ? 'border-green-200/90 bg-green-50/80 dark:border-green-800/50 dark:bg-green-950/25'
            : 'border-amber-200/80 bg-white/90 dark:border-amber-900/40 dark:bg-slate-900/60'
      } ${
        canOpen
          ? paper
            ? 'cursor-pointer transition-colors hover:bg-[#f7f1e8] dark:hover:bg-[#261f1b]'
            : 'cursor-pointer transition-colors hover:border-amber-300 hover:bg-amber-50/70 dark:hover:border-amber-800/60 dark:hover:bg-amber-950/25'
          : ''
      }`}
      dir={direction}
      {...(canOpen
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: handleOpen,
            onKeyDown: (event: React.KeyboardEvent<HTMLLIElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleOpen();
              }
            }
          }
        : {})}
    >
      <div className={`flex gap-2.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
        <div className="flex shrink-0 flex-col items-center gap-1 pt-0.5">
          <StepCircle status={child.status} size="sm" />
        </div>
        <div className="min-w-0 flex-1 text-start">
          <div className={`flex items-start gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <span className="mt-0.5 shrink-0">
              <LessonJourneyContentIcon
                kind="inlineExercise"
                className={paper ? '[&_svg]:text-[#3f2a56] dark:[&_svg]:text-[#d4b48a]' : ''}
                showPassTick={done}
              />
            </span>
            <p className={`min-w-0 flex-1 text-sm font-medium leading-snug ${labelClass}`}>{child.label}</p>
          </div>
          {child.hint ? (
            <p className={`mt-0.5 ps-6 ${journeyCommentClass} ${paper ? '!text-[#8a6a45] dark:!text-[#c4a574]' : ''}`}>{child.hint}</p>
          ) : null}
          {child.onAction && child.actionLabel ? (
            <div
              className="mt-2 ps-6"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <StepActionButton
                step={child}
                isRedoAction={done && Boolean(child.onAction)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function ExpandableInlineExercisesSection({ children }: { children: LessonJourneyStep[] }) {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const paper = useJourneyPaper();
  const panelId = useId();
  const completedCount = children.filter((c) => c.status === 'complete').length;
  const allDone = completedCount === children.length;
  const [open, setOpen] = useState(!allDone);

  return (
    <div
      className={`mt-3 overflow-hidden rounded-xl border ${
        paper
          ? 'border-[#e4d5c3] bg-[#f7f1e8] dark:border-[#3d342c] dark:bg-[#221c18]'
          : 'border-amber-200/70 bg-amber-50/30 dark:border-amber-900/40 dark:bg-amber-950/15'
      }`}
      role="group"
      aria-label={t('journey.inlineRequiredAria')}
      dir={direction}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 px-3 py-2.5 text-start transition-colors sm:px-3.5 ${
          paper ? 'hover:bg-[#efe4d4] dark:hover:bg-[#2a241f]' : 'hover:bg-amber-100/50 dark:hover:bg-amber-950/30'
        } ${isRtl ? 'flex-row-reverse' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <LessonJourneyContentIcon
          kind="inlineExercise"
          className={paper ? '[&_svg]:text-[#3f2a56] dark:[&_svg]:text-[#d4b48a]' : ''}
        />
        <span className={`min-w-0 flex-1 text-sm font-medium ${paper ? 'text-[#3f2a56] dark:text-[#f6efe6]' : 'text-amber-950 dark:text-amber-100'}`}>
          {t('journey.inlineExercisesTitle')}
        </span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          paper
            ? 'bg-[#fffdf8] text-[#8a6a45] dark:bg-[#1c1714] dark:text-[#d4b48a]'
            : 'bg-amber-200/80 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200'
        }`}>
          {completedCount}/{children.length}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ease-out ${
            paper ? 'text-[#8a6a45] dark:text-[#d4b48a]' : 'text-amber-800/90 dark:text-amber-300'
          } ${
            open ? 'rotate-180' : 'rotate-0'
          }`}
          aria-hidden
        />
      </button>
      <CollapsibleFadePanel expanded={open} panelId={panelId}>
        <div className={`border-t px-3 pb-2.5 pt-2.5 sm:px-3.5 sm:pb-3 ${
          paper ? 'border-[#e4d5c3] dark:border-[#3d342c]' : 'border-amber-200/60 dark:border-amber-900/35'
        }`}>
          <p
            className={`mb-2.5 text-start ${paper ? 'text-[#8a6a45] dark:text-[#d4b48a]' : 'text-amber-950/85 dark:text-amber-100/85'} ${journeyCommentAccentClass}`}
          >
            {t('journey.inlineExercisesIntro')}
          </p>
          <ul className="space-y-2">
            {children.map((child) => (
              <InlineExerciseListItem key={child.id} child={child} />
            ))}
          </ul>
        </div>
      </CollapsibleFadePanel>
    </div>
  );
}

function JourneyChildGroup({ children }: { children: LessonJourneyStep[] }) {
  if (!children.length) return null;
  return <ExpandableInlineExercisesSection children={children} />;
}

function JourneyStepRow({
  step,
  isLast,
  lineDoneBelow
}: {
  step: LessonJourneyStep;
  isLast: boolean;
  lineDoneBelow: boolean;
}) {
  const paper = useJourneyPaper();
  const hasChildren = (step.children?.length ?? 0) > 0;
  const showConnectorBelow = !isLast || hasChildren;

  return (
    <li className="flex gap-3 sm:gap-4">
      <div className="flex w-8 shrink-0 flex-col items-center sm:w-9">
        <StepCircle status={step.status} />
        {showConnectorBelow ? (
          <div
            className={`mt-1 w-0.5 flex-1 min-h-[1rem] rounded-full ${connectorColor(lineDoneBelow, paper)}`}
            aria-hidden
          />
        ) : null}
      </div>
      <div className={`min-w-0 flex-1 ${isLast && !hasChildren ? 'pb-0' : 'pb-4 sm:pb-5'}`}>
        <StepBody step={step} />
        {hasChildren ? <JourneyChildGroup children={step.children!} /> : null}
      </div>
    </li>
  );
}

function JourneyProgressBar({
  completed,
  total,
  readyFinal
}: {
  completed: number;
  total: number;
  readyFinal: boolean;
}) {
  const paper = useJourneyPaper();
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const fillClass = paper
    ? completed === total && total > 0
      ? 'bg-[#2f6b4f] dark:bg-[#8fbf9a]'
      : 'bg-[#3f2a56] dark:bg-[#d4b48a]'
    : readyFinal
      ? 'bg-blue-500 dark:bg-blue-400'
      : completed === total && total > 0
        ? 'bg-green-500 dark:bg-green-400'
        : 'bg-indigo-500 dark:bg-indigo-400';

  return (
    <div
      className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${
        paper ? 'bg-[#eadfce] dark:bg-[#3d342c]' : 'bg-indigo-100/90 dark:bg-indigo-950/70'
      }`}
      role="progressbar"
      aria-label={`${completed} / ${total}`}
      aria-valuenow={completed}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      <div
        className={`h-full rounded-full transition-[width] duration-500 ease-out ${fillClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export const LessonJourneyTimeline: React.FC<LessonJourneyTimelineProps> = ({
  steps,
  className = '',
  defaultExpanded = false,
  highlightPulseRequest = 0,
  collapseRequest = 0,
  tourId
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const [homeDesign] = useLearnerHomeDesign();
  const paper = homeDesign === 'mme';
  const panelId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [flashHighlight, setFlashHighlight] = useState(false);
  const [expandState, setExpandState] = useState<JourneyExpandState>(() =>
    defaultExpanded ? 'full' : 'collapsed'
  );

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;
    const ro = new ResizeObserver(() => {
      setContentHeight(contentEl.scrollHeight);
    });
    ro.observe(contentEl);
    setContentHeight(contentEl.scrollHeight);
    return () => ro.disconnect();
  }, [steps]);

  const toggleExpandState = useCallback(() => {
    setExpandState((current) => (current === 'collapsed' ? 'full' : 'collapsed'));
  }, []);

  useEffect(() => {
    if (!highlightPulseRequest) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 35;
    let clearFlashTimer: ReturnType<typeof setTimeout> | null = null;

    const startFlash = () => {
      if (cancelled) return;
      const el = sectionRef.current;
      if (!el) {
        if (attempts < maxAttempts) {
          attempts += 1;
          window.setTimeout(startFlash, 100);
        }
        return;
      }

      // Highlight in place — do not force-expand when collapsed.
      setFlashHighlight(true);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });

      clearFlashTimer = window.setTimeout(() => {
        if (!cancelled) setFlashHighlight(false);
      }, JOURNEY_FLASH_MS);
    };

    const kickoff = window.setTimeout(startFlash, 80);

    return () => {
      cancelled = true;
      clearTimeout(kickoff);
      if (clearFlashTimer != null) clearTimeout(clearFlashTimer);
    };
  }, [highlightPulseRequest]);

  useEffect(() => {
    if (!collapseRequest) return;
    setExpandState('collapsed');
  }, [collapseRequest]);

  if (steps.length === 0) return null;

  const countProgress = (list: LessonJourneyStep[]): { completed: number; total: number } => {
    let completed = 0;
    let total = 0;
    for (const s of list) {
      total += 1;
      if (s.status === 'complete') completed += 1;
      if (s.children?.length) {
        const nested = countProgress(s.children);
        completed += nested.completed;
        total += nested.total;
      }
    }
    return { completed, total };
  };
  const { completed: completedCount, total: totalSteps } = countProgress(steps);
  const readyFinal = Boolean(steps.find((s) => s.id === 'mark-passed' && s.status === 'current'));
  const reviewStep = steps.find((s) => s.id === 'review');
  const hasInlineGate = (reviewStep?.children?.length ?? 0) > 0;
  const allDone = totalSteps > 0 && completedCount === totalSteps;

  const isOpen = expandState === 'full';
  const panelMaxHeightPx = isOpen ? contentHeight : 0;

  return (
    <JourneyPaperContext.Provider value={paper}>
    <div
      className={`overflow-anchor-none ${className} ${
        !isOpen && !paper
          ? 'rounded-xl shadow-sm transition-shadow hover:shadow-md dark:shadow-indigo-950/40'
          : ''
      }`}
    >
      <section
        ref={sectionRef}
        data-lesson-journey-panel
        data-tour-id={tourId}
        dir={direction}
        className={`relative flex flex-col overflow-hidden rounded-xl border overflow-anchor-none ${
          paper
            ? 'border-[#e4d5c3] bg-[#fffdf8] dark:border-[#3d342c] dark:bg-[#1c1714]'
            : isOpen
            ? 'border-indigo-100 bg-gradient-to-br from-indigo-50/90 to-white dark:border-indigo-900/50 dark:from-indigo-950/40 dark:to-gray-900/80'
            : 'border-indigo-200/80 bg-gradient-to-br from-indigo-100/80 via-white to-sky-50/70 ring-1 ring-indigo-100/70 hover:border-indigo-300 dark:border-indigo-800/60 dark:from-indigo-950/55 dark:via-gray-900/90 dark:to-slate-900/80 dark:ring-indigo-900/50 dark:hover:border-indigo-700'
        } ${flashHighlight ? 'lesson-journey-flash' : ''}`}
        aria-label="Lesson progress"
      >
        <button
          type="button"
          data-journey-header
          onClick={toggleExpandState}
          className={`group flex w-full shrink-0 items-start gap-3 rounded-xl px-3 py-1.5 text-start transition-colors sm:gap-3.5 sm:px-4 sm:py-2 ${
            paper
              ? 'hover:bg-[#f7f1e8] dark:hover:bg-[#261f1b]'
              : isOpen
              ? 'rounded-b-none hover:bg-indigo-50/60 dark:hover:bg-indigo-950/30'
              : 'hover:bg-indigo-50/40 dark:hover:bg-indigo-950/25'
          } ${isOpen ? 'rounded-b-none' : ''}`}
          aria-expanded={isOpen}
          aria-controls={panelId}
          aria-label={journeyExpandAriaLabel(isOpen, t)}
        >
          <span
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:h-11 sm:w-11 ${
              paper
                ? allDone
                  ? 'bg-[#e7f0e4] text-[#2f6b4f] dark:bg-[#243028] dark:text-[#8fbf9a]'
                  : 'bg-[#f7f1e8] text-[#3f2a56] dark:bg-[#261f1b] dark:text-[#d4b48a]'
                : allDone
                ? 'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300'
                : readyFinal
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
                  : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
            }`}
            aria-hidden
          >
            <Route className="h-5 w-5 sm:h-[1.35rem] sm:w-[1.35rem]" strokeWidth={2.25} />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className={`text-sm font-semibold sm:text-[0.95rem] ${paper ? 'text-[#3f2a56] dark:text-[#f6efe6]' : 'text-indigo-950 dark:text-indigo-50'}`}>
                {t('journey.title', { defaultValue: 'Your lesson journey' })}
              </p>
              {readyFinal ? (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide sm:text-[11px] ${
                  paper
                    ? 'bg-[#f7f1e8] text-[#8a6a45] dark:bg-[#261f1b] dark:text-[#d4b48a]'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200'
                }`}>
                  {t('journey.readyToMarkPassed', { defaultValue: 'Ready to mark passed' })}
                </span>
              ) : null}
            </div>

            <JourneyProgressBar
              completed={completedCount}
              total={totalSteps}
              readyFinal={readyFinal}
            />

            <div
              className={`mt-1 ${paper ? 'text-[#8a6a45] dark:text-[#d4b48a]' : 'text-indigo-800/75 dark:text-indigo-200/70'} ${journeyCommentAccentClass}`}
            >
              <p className="truncate">
                {t('journey.stepsComplete', {
                  done: completedCount,
                  total: totalSteps,
                  defaultValue: '{{done}} of {{total}} steps complete'
                })}
                {isOpen ? (
                  <span className="hidden sm:inline">
                    {hasInlineGate ? (
                      <> · {t('journey.inlineExercisesRequired')}</>
                    ) : (
                      <> · {t('journey.completeInAnyOrder')}</>
                    )}
                  </span>
                ) : null}
              </p>
              {isOpen ? (
                <p className="mt-0.5 truncate sm:hidden">
                  {hasInlineGate ? t('journey.inlineExercisesRequired') : t('journey.completeInAnyOrder')}
                </p>
              ) : null}
            </div>
          </div>

          <span className={`mt-0.5 inline-grid h-8 shrink-0 place-items-center whitespace-nowrap rounded-lg px-2.5 text-xs font-semibold ${
            paper
              ? 'bg-[#3f2a56] text-[#fff8ee] group-hover:bg-[#4a3264] dark:bg-[#d4b48a] dark:text-[#1c1714] dark:group-hover:bg-[#e4c9a4]'
              : 'bg-indigo-600 text-white shadow-sm group-hover:bg-indigo-700 dark:bg-indigo-500 dark:group-hover:bg-indigo-400 dark:group-hover:text-indigo-950'
          }`}>
            <span className={`col-start-1 row-start-1 ${isOpen ? '' : 'invisible'}`}>
              {t('journey.hideSteps', { defaultValue: direction === 'rtl' ? 'پنهان کردن' : 'Hide' })}
            </span>
            <span className={`col-start-1 row-start-1 ${isOpen ? 'invisible' : ''}`}>
              {t('journey.viewSteps', { defaultValue: 'View steps' })}
            </span>
          </span>
        </button>

        <div
          id={panelId}
          className={`shrink-0 overflow-hidden overflow-anchor-none border-t transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none ${
            paper ? 'border-[#e4d5c3] dark:border-[#3d342c]' : 'border-indigo-100/90 dark:border-indigo-900/40'
          } ${
            isOpen ? 'opacity-100' : 'pointer-events-none border-transparent opacity-0'
          }`}
          style={{ maxHeight: `${panelMaxHeightPx}px` }}
        >
          <div
            ref={contentRef}
            className={`px-3 pb-2 pt-1.5 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none sm:px-4 sm:pb-2.5 sm:pt-2 ${
              isOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
            }`}
          >
            <ol className="list-none m-0 p-0">
              {steps.map((step, index) => {
                const isLast = index === steps.length - 1;
                const childSteps = step.children ?? [];
                const childrenAllDone =
                  childSteps.length === 0 || childSteps.every((c) => c.status === 'complete');
                const lineDoneBelow =
                  !isLast && step.status === 'complete' && childrenAllDone;

                return (
                  <JourneyStepRow
                    key={step.id}
                    step={step}
                    isLast={isLast}
                    lineDoneBelow={lineDoneBelow}
                  />
                );
              })}
            </ol>
          </div>
        </div>
      </section>
    </div>
    </JourneyPaperContext.Provider>
  );
};
